import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../avatars";
import FriendRequests from "../FriendRequests/FriendRequests";
import useFriendSearch from "../../hooks/useFriendSearch";
import { logCustomEvent } from "../../services/analytics/analytics";
import { ANALYTICS_EVENTS } from "../../services/analytics/events";
import "./FriendSearch.css";

const FriendSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    searchTerm,
    setSearchTerm,
    searchResults,
    loading,
    error,
    search,
    clearSearch,
    sendRequest,
    cancelRequest,
    getStatus,
  } = useFriendSearch();

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    search(value);
  };

  const handleUserClick = (userId) => navigate(`/profile/${userId}`);

  const getDisplayName = (u) =>
    u.displayName || u.username || t("friends.anonymous_friend");

  const renderAction = (user) => {
    const status = getStatus(user.id);

    if (status === "loading") {
      return (
        <button className="friend-search-add-btn" disabled>
          {t("common.loading")}
        </button>
      );
    }
    if (status === "self") {
      return (
        <button className="friend-search-add-btn" disabled>
          {t("friends.you")}
        </button>
      );
    }
    if (status === "friends") {
      return (
        <button className="friend-search-add-btn requested" disabled>
          {t("friends.friends")}
        </button>
      );
    }
    if (status === "blocked" || status === "blocked_by") {
      return (
        <button className="friend-search-add-btn" disabled>
          {t("friends.blocked")}
        </button>
      );
    }
    if (status === "pending") {
      // They sent you a request — direct them to the requests section
      return (
        <button
          className="friend-search-add-btn"
          onClick={(e) => {
            e.stopPropagation();
            document
              .getElementById("friend-requests")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          {t("friends.review_request")}
        </button>
      );
    }
    if (status === "requested") {
      return (
        <button
          className="friend-search-add-btn requested"
          onClick={(e) => {
            e.stopPropagation();
            cancelRequest(user.id);
          }}
        >
          {t("friends.cancel_request")}
        </button>
      );
    }
    // none
    return (
      <button
        className="friend-search-add-btn"
        onClick={async (e) => {
          e.stopPropagation();
          const ok = await sendRequest(user.id);
          if (ok)
            logCustomEvent(ANALYTICS_EVENTS.FRIEND_REQUEST_SENT, {
              user_id: user.id,
            });
        }}
      >
        {t("friends.add_friend")}
      </button>
    );
  };

  return (
    <div className="friend-search-container">
      <div id="friend-requests">
        <FriendRequests />
      </div>

      <div className="friend-search-section">
        <div className="friend-search-wrapper">
          <input
            type="text"
            placeholder={t("friends.search_placeholder")}
            value={searchTerm}
            onChange={handleSearch}
            className="friend-search-input"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="friend-search-clear">
              ✕
            </button>
          )}
        </div>

        {error && <div className="friend-search-error">{error}</div>}

        <div className="friend-search-results">
          {loading ? (
            <div className="friend-search-loading">
              {t("friends.searching")}
            </div>
          ) : (
            searchResults.map((user) => (
              <div
                key={user.id}
                className="friend-search-card"
                onClick={() => handleUserClick(user.id)}
                role="button"
                tabIndex="0"
                onKeyPress={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  handleUserClick(user.id)
                }
              >
                <div className="friend-search-user-info">
                  <div className="friend-search-avatar">
                    <Avatar
                      name={getDisplayName(user)}
                      src={user.photoURL}
                      size="fluid"
                      alt={t("friends.profile_picture_alt", {
                        name: getDisplayName(user),
                      })}
                      textColor="var(--background-elevated)"
                    />
                  </div>
                  <div className="friend-search-details">
                    <h3>{getDisplayName(user)}</h3>
                    {user.username && (
                      <p className="friend-search-username">@{user.username}</p>
                    )}
                  </div>
                </div>
                {renderAction(user)}
              </div>
            ))
          )}

          {searchTerm && !loading && searchResults.length === 0 && (
            <div className="friend-search-no-results">
              {t("friends.no_results", { searchTerm })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendSearch;
